import * as I18n from 'i18n-js';
import * as React from 'react';

import { IUserContext, withUserContext } from '../components/user_context_provider';
import ControlledCheckbox from '../components/controlled_checkbox';
import ControlledInput from '../components/controlled_input';
import FormGroup from '../components/form_group';
import FormGroupWithInput from '../components/form_group_with_input';
import OffersNudge from './offers_nudge';
import RecommendedOfferNudge from './recommended_offer_nudge';
import OffersCurrencyConversion from './offers_currency_conversion';
import OfferAmountInputs from './offer_amount_inputs';
import { CombinedSaleAndShippingNotice, TimelineNotice, SalesTaxNotice } from './offer_notices';
import bind from '../bind';
import Currency from '../currency';
import { UserInputOffer } from './negotiation_types';
import {
  core_apimessages_Money,
  core_apimessages_NegotiationParty as NegotiationParty,
  core_apimessages_NegotiationAction as NegotiationAction,
  reverb_config_cache_ExchangeRate as ExchangeRate,
  Listing,
} from '../rql';

export interface IExternalProps {
  error?: string;
  listing: Listing;
  buyerHasAtLeastOneAcceptedOffer: boolean;
  exchangeRates: ExchangeRate[];
  currency: string;
  onOfferChange: (offer: Partial<UserInputOffer>) => void;
  offer: UserInputOffer;
  party: NegotiationParty;
  notices?: React.ComponentType<{
    listing: Listing;
    party: NegotiationParty;
  }>;
  autoFocus?: boolean;
  messageInput?: React.ReactNode;
  showTaxAtCheckoutNotices?: boolean;
  recommendedPriceDropPercent?: number;
}

export type IProps = IExternalProps & IUserContext;

interface IState {
  agreementChecked: boolean;
}

export class OfferCreateFormFields extends React.Component<IProps, IState> {
  constructor(props) {
    super(props);

    this.state = {
      agreementChecked: this.props.buyerHasAtLeastOneAcceptedOffer,
    };
  }

  // This can return undefined if this is an initial offer and the listing does
  // not ship to the buyer's shipping region. In this scenario we are vague
  // about the shipping price (eg "+ Shipping")
  get shippingPriceInShopCurrency(): core_apimessages_Money | undefined {
    const prices = this.props.listing.shipping.shippingPrices;

    if (prices.length > 0) {
      return prices[0].originalRate;
    }
  }

  get messageLabel() {
    if (this.props.party === NegotiationParty.SELLER) {
      return I18n.t('commons.offers.sellerMessageLabel');
    }

    return I18n.t('commons.offers.buyerMessageLabel');
  }

  shouldUseInteractiveNudge(): boolean {
    // First-time offerers always see the interactive RecommendedOfferNudge.
    if (!this.props.buyerHasAtLeastOneAcceptedOffer) { return true; }

    // Returning offerers should only see the interactive RecommendedOfferNudge
    // if a rec is available.
    if (this.props.recommendedPriceDropPercent) {
      return true;
    }

    return false;
  }

  renderOfferError() {
    if (!this.props.error) {
      return null;
    }

    return (
      <p
        className="offers-form__error"
      >
        {this.props.error}
      </p>
    );
  }

  renderNotices() {
    const shop = this.props.listing.shop || { name: '' };
    const OtherNotices = this.props.notices;

    return (
      <div className="offers-form__notices">
        <CombinedSaleAndShippingNotice listing={this.props.listing} />
        {this.props.party === NegotiationParty.BUYER &&
          <TimelineNotice shop={shop} />
        }
        {this.props.showTaxAtCheckoutNotices &&
          <SalesTaxNotice shop={shop} />
        }
        {OtherNotices && <OtherNotices listing={this.props.listing} party={this.props.party} />}
      </div>
    );
  }

  @bind
  handleTermsCheckboxUpdate() {
    this.setState({ agreementChecked: !this.state.agreementChecked });
  }

  get labelTaxHint() {
    if (this.props.listing.taxIncluded) {
      return ` (${this.props.listing.taxIncludedHint})`;
    }

    return '';
  }

  renderMessageForm() {
    if (this.props.messageInput) {
      return this.props.messageInput;
    }

    return (
      <ControlledInput
        className="offers-form__message__textarea"
        updateField={this.props.onOfferChange}
        inputType="textarea"
        fieldName="message"
        inputName="message"
        inputValue={this.props.offer.message}
        required={false}
      />
    );
  }

  renderOfferNudge() {
    if (this.props.party !== NegotiationParty.BUYER) { return null; }

    if (this.shouldUseInteractiveNudge()) {
      return (
        <RecommendedOfferNudge
          offer={this.props.offer}
          onOfferChange={this.props.onOfferChange}
          originalPrice={this.props.listing.price.amount}
          recommendedPriceDropPercent={this.props.recommendedPriceDropPercent}
          currency={this.props.currency}
          listingCondition={this.props.listing.condition}
        />
      );
    }

    return (
      <OffersNudge
        offerAmount={this.props.offer.price}
        originalAmount={this.props.listing.price.amount}
        currency={this.props.currency}
      />
    );
  }

  render() {
    const { shop } = this.props.listing;
    const currencyDescription = Currency.get(this.props.currency);

    return (
      <div>
        {this.renderOfferError()}
        <OfferAmountInputs
          offer={this.props.offer}
          listing={this.props.listing}
          party={this.props.party}
          action={NegotiationAction.CREATE}
          currency={this.props.currency}
          taxable={this.props.listing}
          onOfferChange={this.props.onOfferChange}
          autoFocus={this.props.autoFocus}
        />
        {this.renderOfferNudge()}
        {this.props.party === NegotiationParty.SELLER && (
          <FormGroupWithInput
            label={I18n.t('commons.offers.label.shipping')}
            fieldName="shippingPrice"
            inputName="offer-shipping-price"
            currencyPrefix={currencyDescription.symbol}
            value={this.props.offer.shippingPrice}
            updateField={this.props.onOfferChange}
            disabled={false}
            autoComplete="off"
            numberOnly
            required
          />
        )}
        <OffersCurrencyConversion
          offerAmount={this.props.offer.price}
          shippingPrice={this.shippingPriceInShopCurrency}
          rates={this.props.exchangeRates}
          userCurrency={this.props.currency}
          shopCurrency={shop.currency}
        />

        <FormGroup label={this.messageLabel} tagOptional>
          {this.renderMessageForm()}
        </FormGroup>

        {this.renderNotices()}
        {this.props.party === NegotiationParty.BUYER && (
          <div className="offers-form__terms">
            <ControlledCheckbox
              checked={this.state.agreementChecked}
              fieldName="offers-form-terms-checkbox"
              inputName="offers-form-terms-checkbox"
              required
              updateField={this.handleTermsCheckboxUpdate}
            />
            <label htmlFor="offers-form-terms-checkbox">
              {I18n.t('commons.offers.terms')}
            </label>
          </div>
        )}
      </div>
    );
  }
}

export default withUserContext(OfferCreateFormFields);
